/**
 * usePagination Hook
 * Manages pagination state for admin portal tables
 */

import { useState, useCallback, useMemo } from 'react';

export type PaginationState = {
  page: number;
  pageSize: number;
};

export type UsePaginationReturn = {
  // Current state
  page: number;
  pageSize: number;
  
  // Actions
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: (totalPages: number) => void;
  reset: () => void;
  
  // Computed
  canGoPrevious: boolean;
  canGoNext: (totalPages: number) => boolean;
};

export type UsePaginationOptions = {
  initialPage?: number;
  initialPageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
};

/**
 * Hook for managing pagination state
 * 
 * @param options - Configuration options
 * @returns Pagination state and actions
 * 
 * @example
 * const pagination = usePagination({
 *   initialPage: 1,
 *   initialPageSize: 50,
 *   onPageChange: (page) => console.log('Page changed to', page)
 * });
 * 
 * // Use in component
 * <button onClick={pagination.nextPage}>Next</button>
 */
export function usePagination(
  options: UsePaginationOptions = {},
): UsePaginationReturn {
  const {
    initialPage = 1,
    initialPageSize = 50,
    onPageChange,
    onPageSizeChange,
  } = options;

  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  // Wrapped setters that call callbacks
  const setPage = useCallback(
    (newPage: number) => {
      setPageState(newPage);
      onPageChange?.(newPage);
    },
    [onPageChange],
  );

  const setPageSize = useCallback(
    (newPageSize: number) => {
      setPageSizeState(newPageSize);
      setPageState(1); // Reset to first page when page size changes
      onPageSizeChange?.(newPageSize);
      onPageChange?.(1);
    },
    [onPageChange, onPageSizeChange],
  );

  // Navigation actions
  const nextPage = useCallback(() => {
    setPage(page + 1);
  }, [page, setPage]);

  const previousPage = useCallback(() => {
    setPage(Math.max(1, page - 1));
  }, [page, setPage]);

  const goToFirstPage = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const goToLastPage = useCallback(
    (totalPages: number) => {
      setPage(totalPages);
    },
    [setPage],
  );

  const reset = useCallback(() => {
    setPageState(initialPage);
    setPageSizeState(initialPageSize);
    onPageChange?.(initialPage);
  }, [initialPage, initialPageSize, onPageChange]);

  // Computed values
  const canGoPrevious = useMemo(() => page > 1, [page]);
  
  const canGoNext = useCallback(
    (totalPages: number) => page < totalPages,
    [page],
  );

  return {
    // State
    page,
    pageSize,
    
    // Actions
    setPage,
    setPageSize,
    nextPage,
    previousPage,
    goToFirstPage,
    goToLastPage,
    reset,
    
    // Computed
    canGoPrevious,
    canGoNext,
  };
}
